import gsap from 'gsap'
import PropTypes from 'prop-types'
import { memo, useEffect, useMemo, useRef } from 'react'
import Tooltip from 'components/interface/Tooltip/Tooltip'
import classNames from 'lib/util/classNames'
import noop from 'lib/util/noop'
import styles from './Indicator.module.scss'

/**
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
function Indicator (props) {
  const {
    className,
    currentIndex,
    index,
    isActive,
    isComplete,
    isPaused,
    onAutoplayComplete,
    ...rest
  } = props
  const classNameOutput = useMemo(
    () =>
      classNames([
        className,
        styles.container,
        isActive && styles.active,
        isComplete && styles.viewed
      ]),
    [className, isActive, isComplete]
  )
  const progressRef = useRef()
  const progress = useRef({
    value: 0
  })
  const tl = useRef()
  const currentIsViewed = useRef(false)
  const onAutoplayCompleteRef = useRef(onAutoplayComplete)

  useEffect(() => {
    onAutoplayCompleteRef.current = onAutoplayComplete
  }, [onAutoplayComplete])

  /**
   * Effect: On mount/unmount
   */
  useEffect(() => {
    tl.current = gsap.timeline()

    // Reset for remount
    gsap.set(progressRef.current, {
      x: '-105%'
    })

    progress.current.value = 0

    tl.current.to(progress.current, {
      value: 1,
      delay: 0.3,
      duration: 6, // Adjust the duration as needed,
      ease: 'none',
      onUpdate: () => {
        gsap.set(progressRef.current, {
          x: `${-105 * (1 - progress.current.value)}%`
        })
      },
      onComplete: () => {
        onAutoplayCompleteRef.current()
      }
    })

    tl.current.pause()

    return () => {
      tl.current.kill()
    }
  }, [])

  /**
   * Effect: When the `isActive` state changes
   */
  useEffect(() => {
    if (isActive) {
      tl.current.progress(0)
      tl.current.play()
    } else {
      tl.current.pause()

      if (!currentIsViewed.current) {
        tl.current.progress(0)
      }
    }
  }, [isActive])

  useEffect(() => {
    if (isComplete) {
      tl.current.pause()
    } else {
      tl.current.progress(0)
    }
  }, [isComplete])

  /**
   * Effect: When the `isPaused` status changes
   */
  useEffect(() => {
    if (isActive) {
      if (isPaused) {
        tl.current.pause()
      } else {
        tl.current.play()
      }
    }
  }, [isActive, isPaused])

  return (
    <Tooltip.Target
      Tag="button"
      tooltipContent={
        <Tooltip size="small">
          <Tooltip.Header>View Slide {index + 1}</Tooltip.Header>
        </Tooltip>
      }
      className={classNameOutput}
      {...rest}
    >
      <div className={styles.bar}>
        <div ref={progressRef} className={styles.progress} />
      </div>
    </Tooltip.Target>
  )
}

/** @type {object} */
Indicator.propTypes = {
  index: PropTypes.number,
  className: PropTypes.string,
  currentIndex: PropTypes.number,
  onAutoplayComplete: PropTypes.func,
  isActive: PropTypes.bool,
  isPaused: PropTypes.bool,
  isComplete: PropTypes.bool
}

/** @type {object} */
Indicator.defaultProps = {
  index: 0,
  className: '',
  currentIndex: 0,
  isActive: false,
  onAutoplayComplete: noop,
  isPaused: false,
  isComplete: false
}

// Memoize
export default memo(Indicator)
